import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useNotesManager } from '@/contexts/NotesManager';
import { 
  Search, 
  Plus, 
  Star,
  Clock,
  Folder,
  FileText,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Pin,
  PinOff
} from 'lucide-react';

interface NotesNavigationProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  currentView: string;
  onViewChange: (view: string, folderId?: string) => void;
  selectedFolderId?: string;
  onNewNote: () => void;
  isCollapsed?: boolean;
  className?: string;
}

export function NotesNavigation({
  searchQuery,
  onSearchChange,
  currentView,
  onViewChange,
  selectedFolderId,
  onNewNote,
  isCollapsed: externalCollapsed = false,
  className
}: NotesNavigationProps) {
  const { 
    notes, 
    folders, 
    updateNote,
    getFavoriteNotes, 
    getRecentNotes,
    getNotesInFolder 
  } = useNotesManager();
  
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const isExpanded = isSticky || isHovered;

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Handle drop on folder
  const handleDrop = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    const noteId = e.dataTransfer.getData('text/plain');
    
    if (noteId) {
      updateNote(noteId, { folderId });
      setDragOverFolder(null);
    }
  };

  // Handle drag over folder
  const handleDragOver = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  // Handle drag leave folder
  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const navigationItems = [
    {
      id: 'all',
      label: 'All Notes',
      icon: FileText,
      count: notes.length,
      type: 'all'
    },
    {
      id: 'favorites',
      label: 'Favorites',
      icon: Star,
      count: getFavoriteNotes().length,
      type: 'favorites'
    },
    {
      id: 'recent',
      label: 'Recent',
      icon: Clock,
      count: getRecentNotes().length,
      type: 'recent'
    }
  ];

  const renderFolder = (folder: any, depth = 0) => {
    const hasChildren = folders.some(f => f.parentId === folder.id);
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = currentView === 'folder' && selectedFolderId === folder.id;
    const notesCount = getNotesInFolder(folder.id).length;

    return (
      <div key={folder.id}>
        <Button
          variant="ghost"
          onClick={() => onViewChange('folder', folder.id)}
          onDrop={(e) => handleDrop(e, folder.id)}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDragLeave={handleDragLeave}
          className={cn(
            "w-full justify-start h-8 px-2 font-normal transition-all duration-200",
            isSelected 
              ? "bg-primary/10 text-primary" 
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            dragOverFolder === folder.id && "bg-primary/20 ring-1 ring-primary/50"
          )}
          style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="mr-1 p-0.5 hover:bg-accent/30 rounded-sm"
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          )}
          {!hasChildren && <div className="w-4 mr-1" />}
          
          {isExpanded ? (
            <FolderOpen className="w-4 h-4 mr-2 shrink-0" style={{ color: folder.color }} />
          ) : (
            <Folder className="w-4 h-4 mr-2 shrink-0" style={{ color: folder.color }} />
          )}
          
          <span className="flex-1 text-left truncate text-sm">{folder.name}</span>
          
          {dragOverFolder === folder.id && (
            <div className="text-xs text-primary">Drop here</div>
          )}
          
          {notesCount > 0 && dragOverFolder !== folder.id && (
            <Badge variant="secondary" className="ml-auto h-5 px-1.5 text-xs bg-muted text-muted-foreground">
              {notesCount}
            </Badge>
          )}
        </Button>

        {hasChildren && isExpanded && (
          <div>
            {folders
              .filter(f => f.parentId === folder.id)
              .map(childFolder => renderFolder(childFolder, depth + 1))
            }
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className={cn(
        "h-full flex flex-col bg-gradient-to-b from-background/95 to-background/90 backdrop-blur-sm border-r border-border/30 transition-all duration-300 ease-in-out relative group",
        isExpanded ? "w-64" : "w-12",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Sticky Toggle Button */}
      <div className={cn(
        "absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-200",
        isExpanded && "group-hover:opacity-100"
      )}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsSticky(!isSticky)}
          className="w-6 h-6 p-0 text-muted-foreground hover:text-foreground"
          title={isSticky ? "Unpin sidebar" : "Pin sidebar"}
        >
          {isSticky ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
        </Button>
      </div>

      {/* Search Section */}
      <div className={cn(
        "p-4 border-b border-border/20 transition-all duration-300",
        !isExpanded && "px-2 py-4"
      )}>
        {isExpanded ? (
          <div className="relative animate-fade-in">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 h-9 bg-background/50 border-border/50 focus:border-primary/50"
            />
          </div>
        ) : (
          <div className="flex justify-center">
            <Search className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* New Note Button */}
      <div className={cn(
        "p-4 border-b border-border/20 transition-all duration-300",
        !isExpanded && "px-2 py-4"
      )}>
        <Button
          onClick={onNewNote}
          className={cn(
            "h-9 bg-primary/90 hover:bg-primary text-primary-foreground transition-all duration-300",
            isExpanded ? "w-full" : "w-8 aspect-square p-0"
          )}
          title={!isExpanded ? "New Note" : ""}
        >
          <Plus className={cn("w-4 h-4", isExpanded && "mr-2")} />
          {isExpanded && <span className="animate-fade-in">New Note</span>}
        </Button>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto">
        <div className={cn("p-2", !isExpanded && "px-1")}>
          {/* Quick Access */}
          <div className="mb-4">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.type;
              
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  onClick={() => onViewChange(item.type)}
                  className={cn(
                    "mb-1 font-normal transition-all duration-300 flex items-center",
                    isExpanded ? "w-full justify-start h-8 px-2" : "w-8 h-8 p-0 mx-auto",
                    isActive 
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                  title={!isExpanded ? item.label : ""}
                >
                  <Icon className={cn("w-4 h-4 shrink-0", isExpanded && "mr-2")} />
                  {isExpanded && (
                    <>
                      <span className="flex-1 text-left text-sm animate-fade-in">{item.label}</span>
                      {item.count > 0 && (
                        <Badge variant="secondary" className="ml-auto h-5 px-1.5 text-xs bg-muted text-muted-foreground animate-fade-in">
                          {item.count}
                        </Badge>
                      )}
                    </>
                  )}
                </Button>
              );
            })}
          </div>

          {/* Folders Section */}
          {folders.length > 0 && isExpanded && (
            <div className="animate-fade-in">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
                Folders
              </div>
              <div className="space-y-0.5">
                {folders
                  .filter(f => !f.parentId)
                  .map(folder => renderFolder(folder))
                }
              </div>
            </div>
          )}
          
          {/* Collapsed folders indicator */}
          {folders.length > 0 && !isExpanded && (
            <div className="flex justify-center">
              <Folder className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}